/* ============================================================================
   15 · Uniform Builder — Build Your Uniform
   Figma 9Dhvc9N1v5FWO9b54mpCkN · D Uniform Builder 1752:159100 (1440) ·
   M Uniform Builder 1535:15463 (390) · Uniform Builder — Components 1514:8865.
   ----------------------------------------------------------------------------
   What's being decided here:

     • Colour first. Nothing else in the outfitter means anything until the
       uniform has a colour, so the first decision is a drawer of the PLP's
       colour tiles (Filter Drawer 1752:158640), and a product picked before
       it routes through that drawer and then straight on to Quick Buy.
     • One product per layer, picked from category grids that read as the
       PLP: Builder Product Card (1514:136831) hovers to SELECT SIZE +, opens
       the PDP's Quick Buy as "Add to Uniform" (not to bag), and marks itself
       Added. A second pick in the same layer swaps the first.
     • The outfit as a person, not a list. An AI-generated model (ChatGPT
       image generation, dressed one layer at a time from the navy ghosts)
       crossfades to the shot that matches the outfit, beside the Outfit
       Layer slots that double as anchor links to their categories.
     • Desktop's sticky outfitter against mobile's Sticky Uniform Bar
       (1752:159629): the bar keeps the count, the chips (anchors again) and
       the total in reach, and PREVIEW opens the whole outfitter over the page.
     • Navy is the colour everything was shot in. Any other colour is the PDP
       Color Compare's tint taken per pixel (recolor.js), so ghosts and model
       keep their folds in all twenty colours — a stand-in until each colour
       has its own renders.

   Motion is the Homepage / PLP / PDP vocabulary (mo.js, motion.dev): copy
   rises 16 on --mo-slow, plates settle from 1.04, sections reveal row by row,
   drawers are the 06 cart-drawer choreography, and the spring is spent only
   on affirmative moments — a layer landing in the outfit, the uniform going
   to the bag. The bag is the lab's shared one (src/figs-nav/figs-cart.js):
   the model flies to the header bag, each layer lands as its own line in
   the uniform's colour, and the bag opens on them. Reduced motion keeps
   every fade and drops the travel.
   ========================================================================== */

import { mountFigsNav, addToFigsBag, openFigsBag } from '/src/figs-nav/figs-nav.js';
import {
  animate, inView, stagger, hover, press, MO, state, tween, play, track, teardown,
  setSpeed, clearTracked, SPRING_POP, SPRING_RELEASE, $, $$, mqDesktop, revealOnView,
} from '../10-pdp-prototype/mo.js';
import { bindRoving, swapText, swapButtonLabel, pulse, shake, announce } from '../10-pdp-prototype/ui.js';
import { createDrawer, initQuickBuy } from '../10-pdp-prototype/drawers.js';
import { createFigsEmbroidery, swapSlot } from '/src/figs-embroidery/figs-embroidery.js';
import { SIZE_NAMES } from '../10-pdp-prototype/data.js';
import {
  LAYERS, PRODUCTS, productById, productUrl, isExternal, COLORS, colorBySlug, shotFor, EMBROIDERY, money, moneyShort,
} from './data.js';
import { recolor, prewarm } from './recolor.js';

/* ============================================================
   HEADER — the shared nav, once (outside boot() so Replay never
   mounts it twice), and the compact-bar offset the sticky
   outfitter parks under (the PDP's coupling).
   ============================================================ */
mountFigsNav({
  promo: $('[data-fnav-promo]'),
  masthead: $('[data-fnav-masthead]'),
  theme: 'light',
  homeHref: '/experiments/08-d-homepage/',
});

(function initCompactOffset() {
  const compactBar = document.getElementById('fnav-compact-bar');
  const masthead = document.getElementById('fnav-masthead');
  if (!compactBar || !masthead) return;
  let showing = false;
  const sync = () => document.documentElement.style.setProperty(
    '--fnav-compact-offset', showing ? `${compactBar.offsetHeight}px` : '0px');
  new IntersectionObserver(([e]) => { showing = !e.isIntersecting; sync(); }, { threshold: 0 }).observe(masthead);
  addEventListener('resize', sync);
  sync();
})();

const mqLayout = matchMedia('(min-width: 1024px)'); // two-column outfitter
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const pad = (n) => String(n).padStart(2, '0');

/* ============================================================
   STATE — the colour, one item per layer
   ============================================================ */
const ub = {
  color: null,
  items: Object.fromEntries(LAYERS.map((l) => [l.id, null])),
  pending: null, // a product picked before the colour
  active: null,  // the Active layer: the category in view, else the next required one
  adding: false,
};
const color = () => colorBySlug(ub.color);
const layerOf = (id) => LAYERS.find((l) => l.id === id);
const filledLayers = () => LAYERS.filter((l) => ub.items[l.id]);
const linePrice = (item) => productById(item.id).price + (item.emb?.total || 0);
const embTotal = () => filledLayers().reduce((sum, l) => sum + (ub.items[l.id].emb?.total || 0), 0);
const total = () => filledLayers().reduce((sum, l) => sum + linePrice(ub.items[l.id]), 0);
const outOf = () => LAYERS.length;
const ready = () => LAYERS.filter((l) => l.required).every((l) => ub.items[l.id]);
/* A pick's colour: the uniform's, or the sneaker's own colourway (the pairs
   aren't recoloured). Cards show the product title only (2026-10-15); the
   colour is spoken in labels and announcements, and titles Quick Buy. */
const colorName = (p) => (p.tint ? (color()?.name || 'Navy') : p.colorway);
const suffixOf = (p) => (p.tint && ub.color ? `—${colorName(p)}` : '');
const ghostOf = (p) => recolor(p.ghost, p.tint ? color() : null);
const sizeName = (s) => SIZE_NAMES[s] || s;

/* ============================================================
   1 · CATEGORIES — a Category Header and grid per layer, after
   the Start Here colour step (index.html)
   ============================================================ */
const PLUS = 'assets/ui/icon-plus-16.svg';
const CHECK = 'assets/ui/icon-check-20.svg';

function cardHTML(p) {
  return `
    <article class="ub-card" data-card="${p.id}" data-reveal>
      <button class="ub-card__hit" type="button" aria-haspopup="dialog" data-qb-open="${p.id}" data-card-hit>
        <span class="ub-card__media">
          <img class="ub-card__img" alt="" decoding="async" draggable="false" data-ghost="${p.id}" data-card-img>
          <span class="ub-card__hover" aria-hidden="true" data-card-hover><span class="trim">${cardCta(p)}</span><img src="${PLUS}" alt="" width="16" height="16"></span>
        </span>
      </button>
      <span class="ub-card__badge" data-card-badge hidden><img src="${CHECK}" alt="" width="20" height="20"><span>Added</span></span>
      <div class="ub-card__info">
        <p class="ub-card__name">${p.name}</p>
        <p class="ub-card__price">${moneyShort(p.price)}</p>
        <button class="pdp-link ub-card__cta" type="button" tabindex="-1" aria-hidden="true" data-qb-open="${p.id}">
          <span class="trim">${cardCta(p)}</span><img src="${PLUS}" alt="" width="16" height="16">
        </button>
      </div>
    </article>`;
}

function sectionHTML(l) {
  const items = PRODUCTS.filter((p) => p.layer === l.id);
  return `
    <section class="ub-section" id="layer-${l.id}" data-section="${l.id}" aria-labelledby="layer-${l.id}-title">
      <header class="ub-head" data-head data-reveal>
        <p class="ub-head__step">Layer ${pad(l.step)} / ${pad(LAYERS.length)}</p>
        <div class="ub-head__row">
          <h2 class="ub-head__title" id="layer-${l.id}-title" tabindex="-1">${l.title}</h2>
          <span class="ub-head__done" data-head-done="${l.id}" hidden>Complete</span>
        </div>
      </header>
      <div class="ub-grid" role="list">${items.map(cardHTML).join('')}</div>
    </section>`;
}

function renderCategories() {
  $('[data-categories]').insertAdjacentHTML('beforeend', LAYERS.map(sectionHTML).join(''));
  $$('.ub-card').forEach((c) => c.setAttribute('role', 'listitem'));
}

/* Every ghost on the page in the uniform's colour. A colour change paints
   card by card as each recolour lands, the visible top layer first. */
let paintToken = 0;
async function paintGhosts() {
  const token = ++paintToken;
  for (const p of PRODUCTS) {
    const url = await ghostOf(p);
    if (token !== paintToken) return;
    $$(`[data-ghost="${p.id}"]`).forEach((img) => {
      if (img.getAttribute('src') === url) return;
      const swap = img.hasAttribute('src');
      img.src = url;
      if (swap && !state.reduced) play(img, { opacity: [0.4, 1] }, { duration: MO.base, ease: MO.eStandard });
    });
  }
}
function setGhost(img, p) {
  img.dataset.ghost = p.id;
  ghostOf(p).then((url) => { if (img.dataset.ghost === p.id) img.src = url; });
}

/* the card's text link (mobile) and hover label (desktop): before a colour
   it asks for one (Figma D 1485:145257 · M 1535:15463 dim every ghost to 50%
   until then); an Added card edits its pick */
function cardCta(p) {
  if (!ub.color) return 'Select Color';
  return ub.items[p.layer]?.id === p.id ? 'Edit Size' : 'Select Size';
}

function cardLabel(p) {
  const item = ub.items[p.layer];
  const added = item?.id === p.id;
  if (!ub.color) return `${p.name}, ${moneyShort(p.price)}. Select color`;
  return `${p.name}, ${moneyShort(p.price)}. ${added ? `In your uniform, size ${sizeName(item.size)}. Edit size` : 'Select size'}`;
}

/* ============================================================
   2 · OUTFITTER — Outfit Layer slots (desktop), chips (mobile
   bar), preview tiles; all three are anchors to their layer.
   Top and pants carry the red required asterisk (2119:158886),
   empty or filled; an optional layer shows its × only once a
   product is in it (2119:158188), to take it back out.
   ============================================================ */
const X_ICON = 'assets/ui/layer-close-filled.svg';
const REQ = '<span class="ub-req" aria-hidden="true">*</span>';
const xHTML = (l) => (l.required ? REQ
  : `<button class="ub-slot__x" type="button" data-slot-x="${l.id}" hidden><img src="${X_ICON}" alt="" width="16" height="16"></button>`);

function slotHTML(l) {
  return `
    <li class="ub-slot" data-slot="${l.id}" data-state="empty" data-intro-slot>
      <a class="ub-slot__hit" href="#layer-${l.id}" data-jump="${l.id}">
        <span class="ub-slot__prompt">Select ${l.name}</span>
        <span class="ub-slot__media"><img class="ub-slot__img" alt="" decoding="async" data-slot-img></span>
        <img class="ub-slot__emb" src="assets/ui/icon-embroidery-patch.svg" alt="" width="24" height="24" data-slot-emb hidden>
      </a>
      ${xHTML(l)}
    </li>`;
}
function chipHTML(l) {
  return `
    <a class="ub-chip" href="#layer-${l.id}" data-chip="${l.id}" data-state="empty" data-jump="${l.id}">
      <span class="ub-chip__tile">
        <img class="ub-chip__plus" src="assets/ui/icon-plus-16.svg" alt="" width="16" height="16">
        <img class="ub-chip__img" alt="" decoding="async" data-chip-img>
      </span>
      <span class="ub-chip__label">${l.chip}</span>
    </a>`;
}
function ptileHTML(l) {
  return `
    <div class="ub-ptile" data-ptile="${l.id}" data-state="empty">
      <a class="ub-ptile__hit" href="#layer-${l.id}" data-jump="${l.id}" data-from-preview>
        <span class="ub-ptile__prompt">Select ${l.name}</span>
        <img class="ub-ptile__img" alt="" decoding="async" data-ptile-img>
      </a>
      ${xHTML(l)}
    </div>`;
}

function renderOutfitter() {
  $('[data-slots]').innerHTML = LAYERS.map(slotHTML).join('');
  $('[data-chips]').innerHTML = LAYERS.map(chipHTML).join('');
  $('[data-preview-layers]').innerHTML = LAYERS.map(ptileHTML).join('');
}

/* The outfitter is one fixed size: the viewport under the compact bar it
   sticks below (never past Figma's 899), so while stuck the whole outfit,
   total and CTA fill the screen. Measured on load and resize only, so scrolling never
   resizes the model, the layers or the CTA. The layer column follows the
   outfit's height (Figma: 6 × 120 + 5 × 8 = 760). */
(function initOutfitHeight() {
  const root = document.documentElement;
  const main = $('.ub-outfitter__main');
  new ResizeObserver(([e]) => {
    root.style.setProperty('--ub-outfit-h', `${Math.round(e.contentRect.height)}px`);
  }).observe(main);
  const fit = () => {
    if (!mqLayout.matches) return;
    const bar = document.getElementById('fnav-compact-bar')?.offsetHeight || 0;
    root.style.setProperty('--ub-outfitter-top', `${bar}px`);
  };
  addEventListener('resize', fit);
  mqLayout.addEventListener('change', fit);
  addEventListener('load', fit); // the masthead's final height, once fonts and the logo are in
  fit();
})();

/* ---- the avatar: stacked shots, crossfading. Same pose in every shot, so
   a layer appears to land on the model rather than the photo changing. */
function createAvatar(root) {
  let current = null;
  let token = 0;
  return {
    async show(src, { empty = false, preview = false, label = '' } = {}) {
      root.toggleAttribute('data-empty', empty);
      root.toggleAttribute('data-preview', preview);
      if (label) root.setAttribute('aria-label', label);
      if (current?.dataset.src === src) return;
      const t = ++token;
      const img = new Image();
      img.className = 'ub-avatar__shot';
      img.alt = '';
      img.decoding = 'async';
      img.draggable = false;
      img.dataset.src = src;
      img.src = src;
      try { await img.decode(); } catch (e) { /* show it anyway */ }
      if (t !== token) return;
      const prev = current;
      current = img;
      root.append(img);
      if (!prev) return;
      const fade = tween(img, { opacity: [0, 1] }, { duration: MO.slow, ease: MO.eStandard });
      await Promise.resolve(fade?.finished).catch(() => {});
      if (t === token) [...root.children].forEach((c) => { if (c !== current) c.remove(); });
    },
    get el() { return current; },
  };
}
const avatars = [createAvatar($('[data-avatar="desktop"]')), createAvatar($('[data-avatar="preview"]'))];

let avatarToken = 0;
async function setAvatar() {
  const t = ++avatarToken;
  const empty = !ub.color;
  const picks = filledLayers();
  /* the outfit stands on an invisible mannequin, so with nothing picked there
     is nothing to show: the whole uniform stands in instead, at 40% before a
     colour (under the prompt) and as a faint outline once there is one */
  const preview = !picks.length;
  const shot = shotFor(preview ? LAYERS.map((l) => l.id) : picks.map((l) => l.id));
  const src = empty ? shot.src : await recolor(shot.src, color(), { mask: shot.mask });
  if (t !== avatarToken) return;
  const label = empty ? 'A full navy uniform on an invisible mannequin, waiting for a color'
    : picks.length ? `Your uniform in ${color().name}: ${picks.map((l) => productById(ub.items[l.id].id).name).join(', ')}`
      : `Your uniform in ${color().name}, no layers yet`;
  avatars.forEach((a) => a.show(src, { empty, preview: preview && !empty, label }));
}

/* the shots one pick or removal away from this outfit, recoloured before
   they're asked for (not all 32: each recolour is a full-frame pass) */
function prewarmColor() {
  const c = color();
  if (!c || c.source) return;
  const now = new Set(filledLayers().map((l) => l.id));
  const next = new Set(LAYERS.map((l) => {
    const step = new Set(now);
    if (step.has(l.id)) step.delete(l.id); else step.add(l.id);
    return shotFor([...step]);
  }));
  prewarm([...next].map((s) => [s.src, c, { mask: s.mask }]));
}

/* ---- the Ghosts view (preview arrows): the picks stacked into a figure */
function renderGhostStack() {
  const stack = $('[data-ghost-stack]');
  stack.innerHTML = filledLayers().map((l) => `<img class="ub-ghost ub-ghost--${l.id}" alt="" decoding="async" data-stack="${l.id}">`).join('');
  $$('[data-stack]', stack).forEach((img) => setGhost(img, productById(ub.items[img.dataset.stack].id)));
}

/* ============================================================
   3 · SYNC — one pass that brings every surface to the state:
   cards, slots, chips, tiles, totals, counts, CTAs, model
   ============================================================ */
function setCardAdded(card, on) {
  const was = card.hasAttribute('data-added');
  if (on === was) return;
  const badge = $('[data-card-badge]', card);
  card.toggleAttribute('data-added', on);
  if (on) {
    badge.hidden = false;
    play(badge, { opacity: [0, 1], scale: [0.85, 1] }, state.reduced ? { duration: MO.instant } : SPRING_POP);
  } else {
    badge.hidden = true;
  }
}

function syncLayer(l, { pop = false } = {}) {
  const item = ub.items[l.id];
  const p = item && productById(item.id);
  const st = item ? 'filled' : 'empty';
  const slot = $(`[data-slot="${l.id}"]`);
  const chip = $(`[data-chip="${l.id}"]`);
  const tile = $(`[data-ptile="${l.id}"]`);
  [slot, chip, tile].forEach((el) => { el.dataset.state = st; });
  const what = p ? `${p.name}${suffixOf(p)}, size ${sizeName(item.size)}${item.emb ? ', embroidered' : ''}` : null;
  const req = l.required ? ', required' : '';
  $('[data-jump]', slot).setAttribute('aria-label', p ? `${l.name}${req}: ${what}. Go to ${l.title}` : `Select ${l.name}${req}`);
  chip.setAttribute('aria-label', p ? `${l.name}${req}: ${what}` : `${l.name}${req}: not chosen`);
  $('[data-jump]', tile).setAttribute('aria-label', p ? `${l.name}${req}: ${what}` : `Select ${l.name}${req}`);
  const imgs = [$('[data-slot-img]', slot), $('[data-chip-img]', chip), $('[data-ptile-img]', tile)];
  if (p) imgs.forEach((img) => setGhost(img, p));
  else imgs.forEach((img) => { img.removeAttribute('src'); delete img.dataset.ghost; });
  /* an optional layer's ×: only with a pick in it, to take it back out */
  $$(`[data-slot-x="${l.id}"]`).forEach((x) => {
    x.hidden = !p;
    if (p) x.setAttribute('aria-label', `Remove ${p.name} from your uniform`);
  });
  if (pop && p) {
    imgs.forEach((img) => play(img.closest('.ub-slot__hit, .ub-chip__tile, .ub-ptile__hit'),
      { scale: [0.92, 1] }, state.reduced ? { duration: MO.instant } : SPRING_POP));
  }
  $('[data-slot-emb]', slot).hidden = !item?.emb; // the Embroidery Patch (2119:159213)
  /* the Category Header's superscript COMPLETE (2119:158122): up with a pick,
     gone with it */
  const done = $(`[data-head-done="${l.id}"]`);
  if (done.hidden === Boolean(p)) {
    done.hidden = !p;
    if (p && !state.reduced) play(done, { opacity: [0, 1], y: [MO.riseXs, 0] }, { duration: MO.base, ease: MO.eEntrance });
  }
}

let ctaReady = null;
const NEEDS = { desktop: 'Add Uniform—must have top and pant', bar: 'Add—must have top and pant' };
function syncTotals() {
  const sum = money(total());
  const n = filledLayers().length;
  const of = outOf();
  swapIfChanged($('[data-total-desktop]'), sum);
  swapIfChanged($('[data-total-mobile]'), sum);
  swapIfChanged($('[data-total-preview]'), sum);
  swapIfChanged($('[data-bar-count]'), `${n} of ${of}`);
  /* EMBROIDERY: its share of the total, with REMOVE (2119:166909) */
  const e = embTotal();
  const row = $('[data-emb-row]');
  if (e) swapIfChanged($('[data-emb-sum]'), moneyShort(e));
  if (row.hidden === Boolean(e)) {
    row.hidden = !e;
    if (e && !state.reduced) play(row, { opacity: [0, 1], y: [MO.riseXs, 0] }, { duration: MO.base, ease: MO.eEntrance });
  }
  syncCta();
}
function syncCta({ force = false } = {}) {
  const on = ready();
  $$('[data-add-uniform]').forEach((b) => b.setAttribute('aria-disabled', String(!on)));
  if (ub.adding) return;
  /* disabled, the CTA says what it's waiting for: a top and pants */
  const labels = {
    desktop: on ? 'I’m done—add to bag!' : NEEDS.desktop,
    bar: on ? 'I’m done—add to bag!' : NEEDS.bar,
    preview: 'Add',
  };
  const [desk, bar, prev] = $$('[data-add-uniform] [data-cta-label]');
  const changed = ctaReady !== on;
  ctaReady = on;
  /* the label leaves upward when the CTA changes state; a new total only
     crossfades. The spring stays with the add itself. */
  [[desk, labels.desktop], [bar, labels.bar], [prev, labels.preview]].forEach(([el, text]) => {
    if (el.textContent === text) return;
    if (force) el.textContent = text;
    else if (changed) swapButtonLabel(el, text);
    else swapText(el, text);
  });
}
function swapIfChanged(el, text) {
  if (el.textContent !== text) swapText(el, text);
}

function sync({ pop = null } = {}) {
  $$('[data-card]').forEach((card) => {
    const p = productById(card.dataset.card);
    setCardAdded(card, ub.items[p.layer]?.id === p.id);
    $('[data-card-hit]', card).setAttribute('aria-label', cardLabel(p));
    const cta = cardCta(p);
    $$('.ub-card__cta .trim, [data-card-hover] .trim', card).forEach((t) => { if (t.textContent !== cta) swapText(t, cta); });
  });
  LAYERS.forEach((l) => syncLayer(l, { pop: pop === l.id }));
  syncActive();
  syncTotals();
  renderGhostStack();
  setAvatar();
  syncEmbMarks();
  prewarmColor();
}

/* The embroidery on the outfit: each placement's icon or logo (else its
   stitched text, in its thread) where it sits on the garment, in the
   avatar shot's own frame (1024 × 1536, contained), so it tracks the figure
   at any size. The jacket's sits over the top's. */
const EMB_AT = {
  'left-chest': [0.56, 0.232], 'right-chest': [0.437, 0.232],
  'left-sleeve': [0.712, 0.215], 'right-sleeve': [0.3, 0.215],
};
function syncEmbMarks() {
  const marks = ['tops', 'outerwear'].flatMap((id) => ub.items[id]?.emb?.marks || []);
  const html = marks.map((m) => {
    const [x, y] = EMB_AT[m.place] || EMB_AT['left-chest'];
    const at = `--x:${x};--y:${y}`;
    if (m.art) return `<img class="ub-emb-mark ub-emb-mark--art" src="${m.art}" alt="" style="${at}">`;
    if (m.text) return `<span class="ub-emb-mark ub-emb-mark--text" style="${at};color:${m.thread};font-family:${m.font.replace(/"/g, '&quot;')}">${m.text.map((t) => t.replace(/[<&]/g, '')).join('<br>')}</span>`;
    return '';
  }).join('');
  $$('[data-avatar-emb]').forEach((layer) => {
    if (layer.innerHTML === html) return;
    layer.innerHTML = html;
    if (html && !state.reduced) play($$('.ub-emb-mark', layer), { opacity: [0, 1], scale: [0.8, 1] }, { duration: MO.slow, ease: MO.eEntrance });
  });
}

$('[data-emb-remove]').addEventListener('click', () => {
  filledLayers().forEach((l) => { ub.items[l.id].emb = null; });
  sync();
  announce('Embroidery removed from your uniform.');
});

/* ============================================================
   4 · COLOUR — Select a Color drawer (the PLP's colour tiles on
   the PDP drawer shell) and everything that wears the colour
   ============================================================ */
const colorRoot = $('[data-drawer="color"]');
const colorDrawer = createDrawer(colorRoot);
const colorGrid = $('[data-color-grid]', colorRoot);
let draftColor = null;

/* Figma breaks multi-word names a word a line (Forest Green holds one line) */
const tileName = (name) => (name === 'Forest Green' ? name : name.split(' ').join('<br>'));
colorGrid.innerHTML = COLORS.map((c) => `
  <button type="button" class="filter-color-swatch" role="radio" aria-checked="false" tabindex="-1"
    data-color="${c.slug}" aria-label="${c.name}"
    style="background:${c.hex};color:${c.dark ? 'var(--text-inverse)' : 'var(--text-primary)'}">
    <span class="filter-color-swatch__label"><span class="filter-color-swatch__dot"></span>${tileName(c.name)}</span>
  </button>`).join('');

function markColor(slug) {
  $$('.filter-color-swatch', colorGrid).forEach((t) => {
    const on = t.dataset.color === slug;
    t.classList.toggle('filter-color-swatch--selected', on);
    t.setAttribute('aria-checked', String(on));
    t.tabIndex = on ? 0 : -1;
  });
}
/* the drawer's headline: "Select a Color" until one is chosen (a tile
   tapped, or the uniform already has one), then "Color—Navy" */
const colorTitle = $('#ub-colors-title');
function setColorTitle(slug, { instant = false } = {}) {
  const text = slug ? `Color—${colorBySlug(slug).name}` : 'Select a Color';
  if (colorTitle.textContent === text) return;
  if (instant || state.reduced) colorTitle.textContent = text;
  else swapText(colorTitle, text);
}
bindRoving(colorGrid, (tile) => {
  draftColor = tile.dataset.color;
  markColor(draftColor);
  setColorTitle(draftColor);
  play(tile, { scale: [MO.scalePress, 1] }, state.reduced ? { duration: MO.instant } : SPRING_RELEASE);
  announce(`${colorBySlug(draftColor).name}`);
});

function openColor(trigger) {
  /* opened over the preview, whose page lock made this drawer inert */
  colorRoot.inert = false;
  /* empty until a colour is chosen: no tile pre-marked (2026-10-15) */
  draftColor = ub.color || null;
  markColor(draftColor);
  setColorTitle(ub.color, { instant: true });
  const first = $(draftColor ? `[data-color="${draftColor}"]` : '.filter-color-swatch', colorGrid);
  first.tabIndex = 0; // roving focus needs one way in when nothing is marked
  first.setAttribute('data-autofocus', '');
  colorDrawer.open(trigger, { onDismiss: () => { ub.pending = null; } });
  if (!state.reduced) {
    play($$('.filter-color-swatch', colorGrid), { opacity: [0, 1], y: [MO.riseXs, 0] },
      { duration: MO.base, ease: MO.eEntrance, delay: stagger(0.012, { startDelay: 0.16 }) });
  }
}
colorRoot.addEventListener('drawer:close', () => {
  $$('[data-autofocus]', colorGrid).forEach((t) => t.removeAttribute('data-autofocus'));
  /* back to the preview, if it's still up: its scroll lock again */
  if (preview.isOpen) {
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }
});

$('[data-color-confirm]', colorRoot).addEventListener('click', async () => {
  if (!draftColor) { shake(colorGrid); announce('Pick a color for your uniform.'); return; }
  const pending = ub.pending;
  ub.pending = null;
  const closing = colorDrawer.close();
  setColor(draftColor);
  if (pending) {
    await closing;
    openQuickBuy(productById(pending.id), pending.trigger);
  }
});

function setColor(slug) {
  if (slug === ub.color) return;
  const first = !ub.color;
  ub.color = slug;
  const c = color();
  document.documentElement.style.setProperty('--ub-swatch', c.hex);
  document.documentElement.dataset.ubColor = slug; // the ghosts come up from 50%
  /* Uniform Color Selector → Selected (2119:156579); the bar's and the
     preview's colour */
  const sel = $('[data-colorsel]');
  sel.dataset.state = 'selected';
  swapText($('[data-colorsel-value]'), c.name);
  $('[data-colorsel-action]').textContent = 'Change';
  sel.setAttribute('aria-label', `Uniform color: ${c.name}. Change`);
  $('[data-colortag-name]').textContent = c.name;
  colorTag.setAttribute('aria-label', `Uniform color: ${c.name}. Change`);
  swapText($('[data-bar-color-name]'), c.name);
  $('[data-bar-color]').setAttribute('aria-label', `Uniform color: ${c.name}. Change`);
  $('[data-preview-color]').textContent = c.name;
  const empty = $('[data-outfit-empty]');
  if (first && !empty.hidden) {
    Promise.resolve(play(empty, { opacity: [1, 0], y: [0, -MO.riseXs] }, { duration: MO.quick, ease: MO.eExit })?.finished)
      .then(() => { empty.hidden = true; }); // the bar stays collapsed: its caret opens the chips
  }
  paintGhosts();
  sync();
  syncColorTag();
  showColorway(slug);
  announce(`${c.name} is your uniform color.${c.source ? '' : ' Ghosts and model are recolored from navy.'}`);
}

/* ---- the mobile banner's outfit plate (M 2411:30262, 95×206): the montage
   runs until a colour is chosen, then the plate holds the full outfit in
   that colour — the master's pieces, only recoloured (ChatGPT, 2026-10-18;
   tools/colorway/). Each still fades in over the last on --mo-base, then
   the montage (or the last still) stands down. Reduced: it simply swaps. ---- */
const COLORWAY_STILLS = import.meta.glob('./assets/hero/colorway/*.webp', { eager: true, query: '?url', import: 'default' });
const readoutArt = $('.ub-readout__art');
let colorwayTurn = 0;
function showColorway(slug) {
  const src = COLORWAY_STILLS[`./assets/hero/colorway/${slug}.webp`];
  if (!readoutArt || !src) return;
  const mine = ++colorwayTurn;
  const img = new Image(95, 206);
  img.className = 'ub-readout__color';
  img.alt = `An invisible mannequin wearing a ${colorBySlug(slug).name} FIGS uniform`;
  img.src = src;
  img.decode().catch(() => {}).then(() => {
    if (mine !== colorwayTurn) return; // a later pick owns the plate
    const before = $$('.ub-readout__color', readoutArt);
    readoutArt.append(img);
    const settle = () => {
      if (mine !== colorwayTurn) return;
      before.forEach((n) => n.remove());
      readoutArt.dataset.colorway = slug;
    };
    if (state.reduced) { settle(); return; }
    Promise.resolve(play(img, { opacity: [0, 1] }, { duration: MO.base, ease: MO.eStandard })?.finished).then(settle);
  });
}

$$('[data-open-color]').forEach((b) => b.addEventListener('click', () => openColor(b)));

/* ---- the colour tag (desktop): once there's a colour and the Start Here
   selector has scrolled up out of view, the colour rides at the outfit's
   top-left, so it's always in sight and one click from the drawer. It wipes
   in left to right (clip + 12px drift) and leaves the way it came; scrolling
   back to the selector takes it away. Reduced: it simply appears. ---- */
const colorTag = $('[data-colortag]');
let selectorPassed = false;
let tagShown = false;
let tagToken = 0;
function syncColorTag() {
  const show = Boolean(ub.color) && selectorPassed && mqLayout.matches;
  if (show === tagShown) return;
  tagShown = show;
  const t = ++tagToken;
  if (show) {
    colorTag.hidden = false;
    play(colorTag, { opacity: [0, 1], x: [-12, 0], clipPath: ['inset(0 100% 0 0)', 'inset(0 0% 0 0)'] },
      { duration: MO.base, ease: MO.eEntrance });
  } else {
    Promise.resolve(play(colorTag, { opacity: [1, 0], x: [0, -12], clipPath: ['inset(0 0% 0 0)', 'inset(0 100% 0 0)'] },
      { duration: MO.base, ease: MO.eExit })?.finished)
      .catch(() => {})
      .then(() => { if (t === tagToken) colorTag.hidden = true; });
  }
}
/* "passed": the selector's bottom edge has gone up under the compact bar
   (or off the top). Read on every scroll frame, not on an intersection
   change: a trackpad crosses that line a few px at a time, and a one-off
   check at the crossing could read the selector as still on screen and
   never fire again. */
const colorSel = $('[data-colorsel]');
const compactBar = document.getElementById('fnav-compact-bar');
let tagFrame = 0;
function checkSelector() {
  tagFrame = 0;
  const under = Math.max(0, compactBar?.getBoundingClientRect().bottom || 0);
  selectorPassed = colorSel.getBoundingClientRect().bottom <= under;
  syncColorTag();
}
addEventListener('scroll', () => { if (!tagFrame) tagFrame = requestAnimationFrame(checkSelector); }, { passive: true });
addEventListener('resize', checkSelector);
mqLayout.addEventListener('change', checkSelector);
checkSelector();

/* ============================================================
   5 · QUICK BUY — the PDP's drawer as "Add to Uniform". The
   uniform's colour is locked (no swatch rows), the ghost leads
   the gallery, the embroidery card adds its $28 to the CTA.
   ============================================================ */
const qbRoot = $('[data-drawer="quickbuy"]');
let embPick = null; // the embroiderable product Quick Buy is showing
const quickBuy = initQuickBuy({
  root: qbRoot,
  ctaText: (p) => `Add to Uniform—${moneyShort(p.price + (embPick && p.source === embPick ? femb.total() : 0))}`,
  addedText: 'Added to Uniform',
  onFill: (p) => fillQuickBuy(p),
  onAdd: (p, sel) => addItem(p.source, sel),
  onAdded: () => nextCategory(),
});

/* Quick Buy's gallery is the product on model, never the ghost (ghosts are
   the cards' and the outfit's): the pick in the uniform's colour at slide
   size (assets/onmodel/lg — wearfigs.com's shot of that colour, else its
   navy shot recoloured), then in navy, where the product photography was
   shot, its second photo. The New Balance pairs keep their own photos. */
const galleryOf = (p, c) => {
  if (!p.tint) return p.photos;
  const lead = `assets/onmodel/lg/${p.id}/${c.slug}.webp`;
  return c.source && p.modelPhotos ? [lead, p.photos[1]] : [lead];
};

async function openQuickBuy(p, trigger) {
  const c = color();
  const ghost = await ghostOf(p);
  const item = ub.items[p.layer]?.id === p.id ? ub.items[p.layer] : null;
  quickBuy.open({
    source: p,
    ghost, // the embroidery card's own image
    name: `${p.name}—${colorName(p)}`,
    price: p.price, sizes: p.sizes, types: p.types, typeLabel: p.typeLabel, url: productUrl(p, c.slug),
    layer: p.layer, // the shared Quick Buy picks a signed-in shopper's saved size by category
    color: c.slug, colors: null, slideClass: 'ub-qb__slide',
    gallery: galleryOf(p, c),
  }, trigger, { size: item?.size || (p.sizes.length === 1 ? p.sizes[0] : null), type: item?.type });
}

$('[data-categories]').addEventListener('click', (e) => {
  const t = e.target.closest('[data-qb-open]');
  if (!t) return;
  const p = productById(t.dataset.qbOpen);
  const trigger = t.closest('.ub-card').querySelector('[data-card-hit]');
  if (!ub.color) {
    /* colour first: the drawer, then straight on to this product */
    ub.pending = { id: p.id, trigger };
    announce('Pick your uniform color first.');
    openColor(trigger);
    return;
  }
  openQuickBuy(p, trigger);
});

/* ---- embroidery: Personalize Your FIGS (src/figs-embroidery/, the PDP's
   configurator) — Figma 2119:166659. ADD EMBROIDERY opens it on the pick's
   ghost in the uniform's colour; I'M DONE attaches it, the card becomes
   EMBROIDERY ADDED (SHOW SUMMARY · EDIT · REMOVE; REMOVE takes it all off)
   and the CTA carries the total. ADD TO UNIFORM keeps it on the layer: the outfit shows
   the stitched art on the garment, the layer slot its patch, and checkout
   an EMBROIDERY line with REMOVE. The configurator dresses one garment at a
   time; reopening a pick restores its own placements. ---- */
const embBox = $('[data-ub-emb]', qbRoot);
const embAdd = $('[data-ub-emb-add]', qbRoot);
const embAdded = $('[data-ub-emb-added]', qbRoot);
const femb = createFigsEmbroidery({ onChange: onEmbChange });
femb.card(embAdded, { onEdit: (btn) => femb.open(btn) });
$('[data-ub-emb-on]', qbRoot).addEventListener('click', (e) => femb.open(e.currentTarget));

function onEmbChange(attached, { source }) {
  if (!embPick) return;
  const has = attached.size > 0;
  /* signed in (the nav's log-in flow), the slot holds the card even with
     nothing attached: ADD YOUR EMBROIDERY PREFERENCES (the module) */
  swapEmb(femb.showsCard(), { animate: source !== 'restore' });
  quickBuy.refresh();
  if (source === 'done') announce(has ? `Embroidery added, ${moneyShort(femb.total())}.` : 'Embroidery removed.');
  else if (source === 'remove') announce(has ? `Removed. Embroidery ${moneyShort(femb.total())}.` : 'Embroidery removed.');
}
/* the add-on card and EMBROIDERY ADDED share one slot, as on the PDP:
   swapSlot grows the incoming one from the outgoing one's height, so what's
   below moves once, smoothly. Restoring a pick as Quick Buy opens just
   shows the right one. */
function swapEmb(has, { animate = true } = {}) {
  if (has === !embAdded.hidden) return;
  const [from, to] = has ? [embAdd, embAdded] : [embAdded, embAdd];
  if (animate) { swapSlot(from, to); return; }
  from.hidden = true;
  to.hidden = false;
}

function fillQuickBuy(qp) {
  const p = qp.source;
  embBox.hidden = !p.embroidery;
  $('[data-ub-emb-img]', qbRoot).src = qp.ghost; // the PDP embroidery card's ghost, by design
  const item = ub.items[p.layer]?.id === p.id ? ub.items[p.layer] : null;
  embPick = p.embroidery ? p : null;
  if (embPick) {
    const c = color();
    femb.setGarment({ src: qp.ghost, frame: 'ghost', name: c.name, hex: c.hex });
    femb.restore(item?.emb?.attached || null);
    swapEmb(femb.showsCard(), { animate: false });
  }
  /* VIEW PRODUCT and SIZE CHART: a lab PDP opens here, wearfigs.com in a new tab */
  const chart = $('[data-qb-chart]', qbRoot);
  chart.href = qp.url;
  [$('[data-qb-view]', qbRoot), chart].forEach((a) => {
    const ext = isExternal(qp.url);
    if (ext) { a.target = '_blank'; a.rel = 'noopener'; } else { a.removeAttribute('target'); a.removeAttribute('rel'); }
  });
  $('[data-qb-size-grid]', qbRoot).classList.toggle('ub-sizes--wide', p.sizes.some((s) => s.length > 4));
  if (p.embroidery) typewriter.start();
  else typewriter.stop();
}

/* what a layer keeps of its embroidery: the placements (to reopen them),
   the charge, the bag note and each placement's art for the outfit */
const embSnapshot = () => (femb.has() ? {
  attached: femb.attached, total: femb.total(), note: femb.note(),
  marks: femb.lines().map((l) => ({ place: l.place, part: l.part, art: l.art || null, text: l.text || null, thread: l.thread || null, font: l.font || null })),
} : null);

/* The PDP embroidery card's typewriter (re-created from the SITE WORKSHOP
   GIF: 18 characters at the GIF's own cadence, a 5s loop), while Quick Buy is
   open on an embroiderable product. Runs on motion's clock, so Speed scales it. */
const EMB_TIMES = [130, 230, 370, 500, 630, 770, 870, 1030, 1150, 1270, 1400, 1530, 1630, 1730, 1900, 2015, 2130, 2270];
const typewriter = (() => {
  const el = $('[data-ub-emb-type]', qbRoot);
  const text = EMBROIDERY.preview;
  let clock = null;
  let shown = -1;
  const render = (ms) => {
    let n = 0;
    while (n < EMB_TIMES.length && ms >= EMB_TIMES[n]) n += 1;
    if (n !== shown) { shown = n; el.textContent = text.slice(0, n); }
  };
  return {
    start() {
      this.stop();
      if (state.reduced) { el.textContent = text; return; }
      shown = -1;
      render(0);
      clock = track(animate(0, 5000, { duration: 5, ease: 'linear', repeat: Infinity, onUpdate: render }));
    },
    stop() { clock?.stop(); clock = null; el.textContent = text; },
  };
})();
qbRoot.addEventListener('drawer:close', () => typewriter.stop());

/* ============================================================
   6 · OUTFIT CHANGES — add, remove
   ============================================================ */
/* once Quick Buy has confirmed and gone, the page moves on to the next
   category (desktop and mobile), its title focused; the last layer stays */
let justAdded = null;
function nextCategory() {
  const i = LAYERS.findIndex((l) => l.id === justAdded);
  justAdded = null;
  const next = i >= 0 ? LAYERS[i + 1] : null;
  if (next) jump(next.id);
}

function addItem(p, sel) {
  const l = layerOf(p.layer);
  justAdded = l.id;
  const prev = ub.items[l.id];
  ub.items[l.id] = { id: p.id, size: sel.size, type: sel.type, emb: p.embroidery ? embSnapshot() : null };
  sync({ pop: l.id });
  const swapped = prev && prev.id !== p.id ? ` It replaces the ${productById(prev.id).name}.` : '';
  announce(`${p.name}${suffixOf(p)}, size ${sizeName(sel.size)}${sel.type && p.types.length > 1 ? ` ${sel.type}` : ''}, added to your uniform.${swapped} ${filledLayers().length} of ${outOf()} layers, ${money(total())}.`);
}

function removeItem(id) {
  const item = ub.items[id];
  if (!item) return;
  ub.items[id] = null;
  sync();
  announce(`${productById(item.id).name} removed. ${filledLayers().length} of ${outOf()} layers.`);
}

document.addEventListener('click', (e) => {
  const x = e.target.closest('[data-slot-x]');
  if (x) removeItem(x.dataset.slotX);
});

/* ---- anchors: slots, chips and preview tiles jump to their layer ---- */
async function jump(id, { fromPreview = false } = {}) {
  if (fromPreview && preview.isOpen) await preview.close();
  /* land below the compact bar, which is up by the time we arrive */
  const section = $(`#layer-${id}`);
  const offset = document.getElementById('fnav-compact-bar')?.offsetHeight || 0;
  const top = section.getBoundingClientRect().top + scrollY - offset;
  window.scrollTo({ top: Math.max(0, top), behavior: state.reduced ? 'auto' : 'smooth' });
  $(`#layer-${id}-title`).focus({ preventScroll: true });
  history.replaceState(null, '', `#layer-${id}`);
}
document.addEventListener('click', (e) => {
  const a = e.target.closest('[data-jump]');
  if (!a) return;
  e.preventDefault();
  jump(a.dataset.jump, { fromPreview: a.hasAttribute('data-from-preview') });
});

/* ---- Active slot / chip (2119:156630: white, 2px Black). The category in
   view is Active; in the 48px gaps between mobile sections the last one
   stays. Above the first layer (the hero, Start Here) the next required
   layer to fill is, once there's a colour: top, then pants (Figma 2119:156553
   → 2119:157686), and none when both are in. ---- */
let spyLayer = null;
function syncActive() {
  const next = ub.color ? LAYERS.find((l) => l.required && !ub.items[l.id])?.id ?? null : null;
  const active = spyLayer || next;
  if (active === ub.active) return;
  ub.active = active;
  $$('[data-slot], [data-chip]').forEach((el) => el.toggleAttribute('data-active', (el.dataset.slot || el.dataset.chip) === active));
}
function initSpy() {
  const visible = new Set();
  const first = $('[data-section]');
  const io = new IntersectionObserver((entries) => {
    entries.forEach((en) => (en.isIntersecting ? visible.add(en.target.dataset.section) : visible.delete(en.target.dataset.section)));
    const inBand = LAYERS.find((l) => visible.has(l.id))?.id;
    spyLayer = inBand || (first.getBoundingClientRect().top < innerHeight / 2 ? spyLayer : null);
    syncActive();
  }, { rootMargin: '-45% 0px -50% 0px' });
  $$('[data-section]').forEach((s) => io.observe(s));
}

/* ============================================================
   7 · STICKY UNIFORM BAR + PREVIEW (mobile)
   ============================================================ */
const bar = $('[data-bar]');
const barToggle = $('[data-bar-toggle]');
const barChips = $('[data-bar-chips]');

/* The tray opens like the PDP's drawers: it grows on --mo-slow / entrance
   while the chips slide in from the side, staggered (the PDP Quick Buy
   gallery's x 24 → 0), and leaves decisively on --mo-base / exit. The bar's
   12px gap is folded into the tray (margin -12 → 0) so nothing jumps. Reduced:
   no travel, the chips fade. */
let barAnim = 0;
async function setBar(expanded) {
  if ((bar.dataset.state === 'expanded') === expanded) return;
  const token = ++barAnim;
  bar.dataset.state = expanded ? 'expanded' : 'closed';
  barToggle.setAttribute('aria-expanded', String(expanded));
  const chips = $$('.ub-chip', barChips);
  if (expanded) {
    barChips.hidden = false;
    const h = barChips.scrollHeight;
    if (state.reduced) {
      tween(chips, { opacity: [0, 1] }, { duration: MO.base, ease: MO.eStandard });
      return;
    }
    play(barChips, { height: [0, h], marginTop: [-12, 0] }, { duration: MO.slow, ease: MO.eEntrance })
      ?.finished.then(() => { if (token === barAnim) { barChips.style.height = ''; barChips.style.marginTop = ''; } });
    play(chips, { opacity: [0, 1], x: [24, 0] },
      { duration: MO.slow, ease: MO.eEntrance, delay: stagger(MO.stagTight, { startDelay: 0.08 }) });
  } else {
    if (!state.reduced) {
      play(chips, { opacity: [1, 0] }, { duration: MO.quick, ease: MO.eExit });
      await Promise.resolve(play(barChips, { height: [barChips.offsetHeight, 0], marginTop: [0, -12] },
        { duration: MO.base, ease: MO.eExit })?.finished).catch(() => {});
    }
    if (token !== barAnim) return;
    barChips.hidden = true;
    barChips.style.height = '';
    barChips.style.marginTop = '';
    chips.forEach((c) => { c.style.opacity = ''; c.style.transform = ''; });
  }
}
barToggle.addEventListener('click', () => setBar(bar.dataset.state !== 'expanded'));
new ResizeObserver(() => {
  const h = mqLayout.matches ? 0 : bar.offsetHeight;
  document.documentElement.style.setProperty('--ub-bar-h', `${h}px`);
}).observe(bar);

const previewRoot = $('[data-drawer="preview"]');
const preview = createDrawer(previewRoot);
const VIEWS = ['model', 'ghosts'];
let view = 0;
/* the preview sits under whatever header is showing, like Figma's frame */
function headerBottom() {
  let bottom = 0;
  ['fnav-compact-bar', 'fnav-masthead'].forEach((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.height && r.bottom > 0 && r.top < innerHeight / 2) bottom = Math.max(bottom, r.bottom);
  });
  const promo = $('[data-fnav-promo]')?.firstElementChild;
  if (promo) { const r = promo.getBoundingClientRect(); if (r.bottom > bottom && r.top >= 0) bottom = Math.max(bottom, r.bottom); }
  return Math.round(bottom);
}
function setView(i, { instant = false } = {}) {
  view = (i + VIEWS.length) % VIEWS.length;
  const model = $('[data-avatar="preview"]');
  const ghosts = $('[data-ghost-stack]');
  const [show, hide] = VIEWS[view] === 'model' ? [model, ghosts] : [ghosts, model];
  const other = VIEWS[(view + 1) % VIEWS.length] === 'model' ? 'Show the model' : 'Show the ghosts';
  $$('[data-view-step]', previewRoot).forEach((b) => b.setAttribute('aria-label', other));
  $('[data-view-label]', previewRoot).textContent = VIEWS[view] === 'model' ? 'Model view' : 'Ghosts view';
  show.hidden = false;
  if (instant) { hide.hidden = true; show.style.opacity = ''; return; }
  play(hide, { opacity: [1, 0] }, { duration: MO.quick, ease: MO.eExit })?.finished.then(() => { hide.hidden = true; });
  play(show, { opacity: [0, 1] }, { duration: MO.base, ease: MO.eEntrance });
}
$$('[data-view-step]', previewRoot).forEach((b) => b.addEventListener('click', () => setView(view + Number(b.dataset.viewStep))));

$('[data-open-preview]').addEventListener('click', (e) => {
  previewRoot.style.setProperty('--ub-preview-top', `${headerBottom()}px`);
  setView(0, { instant: true });
  preview.open(e.currentTarget);
  if (!state.reduced) {
    tween($('[data-preview-view]', previewRoot), { opacity: [0, 1], scale: [MO.scaleIn, 1] },
      { duration: MO.deliberate, ease: MO.eEntrance, delay: 0.12 });
    play($$('.ub-ptile', previewRoot), { opacity: [0, 1], y: [MO.riseXs, 0] },
      { duration: MO.base, ease: MO.eEntrance, delay: stagger(MO.stagTight, { startDelay: 0.2 }) });
  }
});
/* the preview is mobile's; crossing to desktop closes it */
mqLayout.addEventListener('change', (e) => { if (e.matches) preview.close(); });

/* ============================================================
   8 · ADD UNIFORM TO BAG — every pick, one bag bump. The spring
   is licensed here, as on the PDP's ADD. Each layer is its own bag
   line — never one grouped "uniform" — shown on model in the
   uniform's colour (assets/onmodel: wearfigs.com's shot of that
   colour, else its navy shot recoloured), never the ghost; the
   New Balance pairs keep their own photo. Embroidery is in the price.
   ============================================================ */
const onModel = (p, slug) => `assets/onmodel/${p.id}/${slug}.webp`;
/* an embroidered line carries its garment for the bag's embroidery (the
   accordion's stitched thumbnail, EDIT): the ghost in the uniform's colour,
   kept as a small image — the recoloured one is a blob that dies with the page */
async function keepGhost(p) {
  const img = new Image();
  img.src = await ghostOf(p);
  await img.decode();
  const c = document.createElement('canvas');
  c.width = c.height = 400;
  c.getContext('2d').drawImage(img, 0, 0, 400, 400);
  return c.toDataURL('image/webp', 0.85);
}
const bagLines = () => Promise.all(filledLayers().map(async (l) => {
  const item = ub.items[l.id];
  const p = productById(item.id);
  const line = {
    id: p.id, name: p.name, color: colorName(p), size: item.size, type: item.type,
    price: p.price,
    image: p.tint ? onModel(p, ub.color || 'navy') : p.photos[0],
    fallback: p.tint ? onModel(p, 'navy') : '',
    url: p.lab ? productUrl(p, ub.color) : '', // the bag thumbnail links back to a lab PDP
  };
  /* the bag prices and shows the embroidery itself: FINAL SALE, the
     Embroidery accordion (EDIT, REMOVE per charge), its charges in the
     subtotal (src/figs-embroidery/ bagLine, src/figs-nav/figs-cart.js) */
  if (item.emb) {
    const c = color();
    line.embroidery = femb.bagLine(item.emb.attached, { src: await keepGhost(p), frame: 'ghost', name: c.name, hex: c.hex });
  }
  return line;
}));

$$('[data-add-uniform]').forEach((btn) => btn.addEventListener('click', async () => {
  if (ub.adding) return;
  if (!ub.color) { openColor(btn); return; }
  if (!ready()) {
    const missing = LAYERS.filter((l) => l.required && !ub.items[l.id]);
    shake(btn);
    announce(`Add ${missing.map((l) => `a ${l.name.toLowerCase()}`).join(' and ')} to finish your uniform.`);
    if (preview.isOpen) await preview.close();
    jump(missing[0].id);
    return;
  }
  ub.adding = true;
  const n = filledLayers().length;
  const summary = `Your ${color().name} uniform, ${n} items, ${money(total())}, is in your bag.`;
  /* the model on screen flies to the header bag; from the mobile preview
     the preview closes before the bag opens, so the two never stack */
  bagLines()
    .then((lines) => addToFigsBag(lines, { from: [avatars[0].el, avatars[1].el, btn], open: false }))
    .then(async (count) => {
      announce(`${summary} ${count} ${count === 1 ? 'item' : 'items'} in bag.`);
      if (preview.isOpen) await preview.close();
      openFigsBag();
    });
  pulse(btn);
  await Promise.all($$('[data-add-uniform] [data-cta-label]').map((l) => swapButtonLabel(l, 'Added to Bag', { affirm: true })));
  await wait(state.reduced ? 900 : 1600);
  ub.adding = false;
  ctaReady = !ready(); // read as a state change, so the label rises back in
  syncCta();
}));

/* ============================================================
   9 · FOOTER — the PDP / Homepage accordion (row layout from 1024)
   ============================================================ */
const mqFooterRow = matchMedia('(min-width: 1024px)');
function initFooter() {
  const OPEN = '<path d="M1 5h8"/>';
  const CLOSED = '<path d="M5 1v8M1 5h8"/>';
  $$('[data-fgroup]').forEach((group) => {
    const btn = $('[data-fgroup-toggle]', group);
    const list = $('[data-fgroup-list]', group);
    const icon = $('[data-fgroup-icon]', group);
    btn.addEventListener('click', () => {
      if (mqFooterRow.matches) return;
      const open = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!open));
      list.hidden = open;
      icon.innerHTML = open ? CLOSED : OPEN;
      if (!open) play(list, { opacity: [0, 1], y: [-4, 0] }, { duration: MO.base, ease: MO.eEntrance });
    });
  });
}

/* ============================================================
   10 · FIRST SCREEN + REVEALS — the Homepage / PLP / PDP recipes:
   the hero's gradient fades and settles from 1.04 with the looks
   rising over it, copy rises 16 on --mo-slow with the tight
   stagger, the outfit plate fades and settles, slots ripple in,
   the bar rises; each Category Header and its cards reveal on
   view, row by row.
   ============================================================ */
let bootAt = 0;
const INTRO = 0.12;
const introDelay = (d) => () => (performance.now() - bootAt < 500 ? d : 0);

/* Uniform File Format Banner: today's date and the local time (minute by
   minute), typed in line by line on load like a terminal, READY last.
   Reduced motion: the lines are simply there. */
const readoutLines = $$('[data-readout-line]');
function readoutText() {
  const now = new Date();
  const p2 = (n) => String(n).padStart(2, '0');
  const tz = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(now).find((x) => x.type === 'timeZoneName')?.value || '';
  const h12 = now.getHours() % 12 || 12;
  $('[data-readout-date]').dataset.text = `Stats: ${p2(now.getMonth() + 1)}/${p2(now.getDate())}/${now.getFullYear()}`;
  $('[data-readout-time]').dataset.text = `${p2(h12)}: ${p2(now.getMinutes())} ${tz} Local`;
  readoutLines.forEach((l) => { if (!l.dataset.text) l.dataset.text = l.textContent.trim(); });
}
let readoutToken = 0;
async function typeReadout() {
  const t = ++readoutToken;
  readoutText();
  $('[data-readout-ready]').removeAttribute('data-rec');
  if (state.reduced) { readoutLines.forEach((l) => { l.textContent = l.dataset.text; }); return; }
  readoutLines.forEach((l) => { l.textContent = ''; });
  await wait((INTRO + 0.1) * 1000);
  /* desktop reads the row left to right (READY before the time) */
  const order = mqLayout.matches ? [0, 1, 2, 4, 3] : [0, 1, 2, 3, 4];
  for (const i of order) {
    const l = readoutLines[i];
    l.setAttribute('data-typing', '');
    for (const ch of l.dataset.text) {
      if (t !== readoutToken) return;
      l.textContent += ch;
      await wait(16);
    }
    l.removeAttribute('data-typing');
  }
  /* READY then blinks like a camcorder's REC light: slow, hard on / off (CSS) */
  $('[data-readout-ready]').setAttribute('data-rec', '');
}
setInterval(() => {
  readoutText();
  const time = $('[data-readout-time]');
  if (!time.hasAttribute('data-typing') && time.textContent) time.textContent = time.dataset.text;
}, 30000);

function initIntro() {
  const rise = { y: [MO.riseSm, 0], opacity: [0, 1] };
  typeReadout();
  tween($('[data-hero-bg]'), { opacity: [0, 1], scale: [MO.scaleIn, 1] }, { duration: MO.cinematic, ease: MO.eEntrance });
  tween($('[data-hero-models]'), { opacity: [0, 1], y: [MO.riseMd, 0] }, { duration: MO.deliberate, ease: MO.eEntrance, delay: INTRO + 0.08 });
  tween($$('[data-intro]'), rise, { duration: MO.slow, ease: MO.eEntrance, delay: stagger(MO.stagTight, { startDelay: INTRO }) });
  const plate = $('[data-intro-plate]');
  tween(plate, { opacity: [0, 1] }, { duration: MO.deliberate, ease: MO.eEntrance, delay: INTRO });
  tween($('[data-avatar="desktop"]'), { scale: [MO.scaleIn, 1] }, { duration: MO.cinematic, ease: MO.eEntrance, delay: INTRO });
  tween($$('[data-intro-slot]'), { opacity: [0, 1], y: [MO.riseXs, 0] },
    { duration: MO.base, ease: MO.eEntrance, delay: stagger(MO.stagTight, { startDelay: INTRO + 0.24 }) });
  tween(bar, { opacity: [0, 1], y: [MO.riseMd, 0] }, { duration: MO.slow, ease: MO.eEntrance, delay: INTRO + 0.3 });
}

function initReveals() {
  const rise = (d) => ({ y: [d, 0], opacity: [0, 1] });
  revealOnView($$('[data-head], .ub-section__color'), rise(MO.riseSm), { duration: MO.slow, startDelay: introDelay(INTRO + 0.2) });
  revealOnView($$('[data-card]'), rise(MO.riseMd), { duration: MO.deliberate, startDelay: introDelay(INTRO + 0.28) });
  teardown.push(inView($('[data-footer]'), () => {
    tween($$('[data-footer-item]'), rise(MO.riseMd), { duration: MO.deliberate, delay: stagger(MO.stagBase), ease: MO.eEntrance });
  }, { amount: 0.1 }));
}

/* Desktop·Hover (1613:177403): the ghost to 50%, SELECT SIZE + over it.
   motion's hover() ignores touch, so mobile keeps its always-on row. */
function initCardHover() {
  $$('[data-card]').forEach((card) => {
    const img = $('[data-card-img]', card);
    const cta = $('[data-card-hover]', card);
    let on = false;
    let ins = [];
    /* hand the rest state back to the stylesheet (50% before a colour, the
       focus-visible layer). A hover the colour drawer cuts short in its first
       frames left the label stuck at opacity 0: the label's y rise was still
       running and its last frame rewrote the inline styles after the reset. So
       the out stops the in, and the reset waits for anything still running. */
    const release = () => {
      if (on) return;
      if ([img, cta].some((el) => el.getAnimations().some((a) => a.playState === 'running'))) { setTimeout(release, 60); return; }
      [img, cta].forEach((el) => { el.style.opacity = ''; el.style.transform = ''; });
    };
    teardown.push(hover(card, () => {
      if (!mqLayout.matches) return undefined;
      on = true;
      ins = [
        track(animate(img, { opacity: 0.5 }, { duration: MO.base, ease: MO.eStandard })),
        track(animate(cta, { opacity: 1, y: [MO.riseXs, 0] }, { duration: MO.base, ease: MO.eEntrance })),
      ];
      return () => {
        on = false;
        ins.forEach((a) => a?.stop?.());
        const out = [
          track(animate(img, { opacity: ub.color ? 1 : 0.5 }, { duration: MO.quick, ease: MO.eStandard })),
          track(animate(cta, { opacity: 0 }, { duration: MO.quick, ease: MO.eStandard })),
        ];
        Promise.all(out.map((a) => a?.finished)).then(release, release);
        setTimeout(release, (MO.quick / (state.speed || 1)) * 1000 + 60);
      };
    }));
  });
}

/* Shared micro-interactions — the Homepage's data-hover-link / data-press */
function initMicro() {
  $$('[data-hover-link]').forEach((l) => {
    teardown.push(hover(l, () => {
      track(animate(l, { y: -2, opacity: 0.82 }, { duration: MO.quick, ease: MO.eEntrance }));
      return () => track(animate(l, { y: 0, opacity: 1 }, { duration: MO.quick, ease: MO.eEntrance }));
    }));
  });
  [...$$('[data-press]'), ...$$('[data-add-uniform]'), $('[data-qb-atb]'), $('[data-color-confirm]')].filter(Boolean).forEach((el) => {
    teardown.push(press(el, () => {
      if (el.getAttribute('aria-disabled') === 'true') return undefined;
      track(animate(el, { scale: MO.scalePress }, { duration: MO.instant, ease: MO.eStandard }));
      return () => track(animate(el, { scale: 1 }, state.reduced ? { duration: MO.instant } : SPRING_RELEASE));
    }));
  });
}

/* ============================================================
   LAB BAR — the Homepage's motion.dev toolbar (hidden; H shows
   it). Speed scales every tracked control; Replay / Reduced tear
   down and rebuild the motion layer.
   ============================================================ */
function initLabBar() {
  const labBar = $('[data-lab-bar]');
  const handle = $('[data-lab-handle]');
  const slider = $('#lab-speed');
  const readout = $('[data-readout]');
  const syncOffset = () => {
    const h = !labBar.isConnected || labBar.hidden || mqDesktop.matches ? 0 : labBar.offsetHeight + 8;
    document.documentElement.style.setProperty('--lab-offset', `${h}px`);
  };
  slider.addEventListener('input', () => {
    const v = parseFloat(slider.value);
    readout.textContent = `${v.toFixed(2)}x`;
    setSpeed(v);
  });
  const rebuild = () => {
    [colorDrawer, quickBuy, preview].forEach((d) => d?.close());
    window.scrollTo({ top: 0, behavior: 'instant' });
    teardown.splice(0).forEach((fn) => { try { fn?.(); } catch (e) { /* ignore */ } });
    clearTracked();
    const pre = $$('[data-reveal], [data-intro], [data-intro-plate], [data-intro-slot], [data-footer-item], [data-hero-bg], [data-hero-models]');
    pre.forEach((el) => { el.style.opacity = '0'; });
    animate(pre, { opacity: 0 }, { duration: 0 });
    requestAnimationFrame(boot);
  };
  $('[data-replay]').addEventListener('click', rebuild);
  const rb = $('[data-reduced-btn]');
  rb.setAttribute('aria-pressed', String(state.reduced));
  rb.addEventListener('click', () => {
    state.reduced = !state.reduced;
    rb.setAttribute('aria-pressed', String(state.reduced));
    rebuild();
  });
  const gb = $('[data-grid-btn]');
  const ov = $('[data-grid-overlay]');
  gb.addEventListener('click', () => {
    const on = gb.getAttribute('aria-pressed') !== 'true';
    gb.setAttribute('aria-pressed', String(on));
    ov.dataset.on = String(on);
  });
  let killed = false;
  $('[data-hide-btn]').addEventListener('click', () => {
    killed = true;
    handle.hidden = true;
    labBar.remove();
    syncOffset();
  });
  addEventListener('keydown', (e) => {
    if (killed) return;
    if (e.key.toLowerCase() !== 'h' || e.metaKey || e.ctrlKey || e.altKey) return;
    if (/^(input|textarea|select)$/i.test(e.target.tagName)) return;
    const hide = !labBar.hidden;
    labBar.hidden = hide;
    handle.hidden = !hide;
    syncOffset();
  });
  handle.addEventListener('click', () => {
    if (killed) return;
    labBar.hidden = false;
    handle.hidden = true;
    syncOffset();
  });
  addEventListener('resize', syncOffset);
  mqDesktop.addEventListener('change', syncOffset);
  syncOffset();
  tween(labBar, { y: [MO.riseMd, 0], opacity: [0, 1] }, { duration: MO.slow, delay: 0.4, ease: MO.eEntrance });
}

/* ============================================================
   BOOT — one-time bindings first, then the replayable motion layer
   ============================================================ */
function initOnce() {
  renderCategories();
  renderOutfitter();
  initSpy();
  initFooter();
  paintGhosts();
  sync();
}

function boot() {
  document.documentElement.dataset.reduced = String(state.reduced);
  bootAt = performance.now();
  initIntro();
  initReveals();
  initCardHover();
  initMicro();
}

initOnce();
boot();
document.documentElement.setAttribute('data-mo-ready', '');
initLabBar();

/* a deep link to a layer (#layer-pants) lands on it */
if (/^#layer-/.test(location.hash)) {
  const id = location.hash.slice(7);
  if (layerOf(id)) requestAnimationFrame(() => $(`#layer-${id}`).scrollIntoView({ block: 'start' }));
}

addEventListener('load', () => {
  const broken = $$('img').filter((i) => i.getAttribute('src') && i.complete && i.naturalWidth === 0);
  if (broken.length) console.error(`[assets] ${broken.length} image(s) failed to load`, broken.map((i) => i.src));
  else console.info(`[assets] ${$$('img').length} images OK`);
});
console.info('%cFIGS Motion Lab · 15 Uniform Builder', 'font-weight:700',
  '\nMotion: motion.dev v12 (vanilla) via the PDP bridge', '\nReduced:', state.reduced);
